import { Stack, Divider } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useSWR from 'swr';

import EditAvailabilityModal from './calendar/editAvailability';
import CalendarView from './calendarView';
import { convertTimeRangeHourFormat } from './common/helpers';
import SwitchViewComponent from './common/switchView';
import ListCalendarView from './listCalendarView';

import { ROLE_VALUES } from '../../../constants/customerConstant';
import { useAuth } from '../../../contexts/auth/authContext';
import { useToastNotificationContext } from '../../../hooks/global/alert/useToastNotification';
import {
  AvailabilityObject,
  IntervalsObject,
} from '../../../interfaces/meeting';
import { setTenantId } from '../../../slices/meetings';

interface OriginalCalendarData {
  [key: number]: { from: string; to: string }[];
}

export interface AvailabilityModalObject {
  date: string | null;
  isOpen: boolean;
  intervals: IntervalsObject[];
}

function transformCalendarDataData(data: OriginalCalendarData) {
  const result = [];

  for (const weekday in data) {
    let transformedWeekday;
    if (parseInt(weekday) <= 7)
      transformedWeekday = (parseInt(weekday) + 1) % 7;
    else transformedWeekday = weekday;
    const intervals = data[weekday].map(({ from, to }) => ({ from, to }));
    result.push({
      weekday: transformedWeekday.toString(),
      intervals: intervals,
    });
  }
  return result;
}

const AvailabilityCalendar = () => {
  const dispatch = useDispatch();
  const { slug } = useAuth();
  const userType = localStorage.getItem('user_type');
  const { createToastNotification } = useToastNotificationContext();
  const isAdmin =
    userType === `${ROLE_VALUES.TENANT_ADMIN}` ||
    userType === `${ROLE_VALUES.PARTNER_ADMIN}`;
  const containterRef = useRef<HTMLDivElement>(null);
  const [companySlug, setCompanySlug] = useState<string | null>(slug);

  const [view, setView] = useState<string>('calendar');
  const [currentMonth, setCurrentMonth] = useState<string | null>();
  const [currentYear, setCurrentYear] = useState<string | null>();
  const [calendarSchedule, setCalendarSchedule] = useState<
  AvailabilityObject[]
  >([]);
  const [availability, setAvailability] = useState<AvailabilityModalObject>({
    date: '',
    isOpen: false,
    intervals: [],
  });

  useEffect(() => {
    if (!slug) {
      setCompanySlug(localStorage.getItem('slug'));
    }
  }, [slug]);

  const { data, isLoading, mutate } = useSWR(
    (companySlug && currentMonth && currentYear) ? `schedule/availability/tenant/${companySlug}/calendar?month=${currentMonth}&year=${currentYear}` : '',
    { revalidateOnFocus: true },
  );

  const setMonthYear = (date: string) => {
    try {
      const [month, year] = date.split(' ');
      const dateString = new Date(`${month} 1, ${year}`);
      const monthNumber = dateString.getMonth() + 1;
      setCurrentMonth(`${monthNumber}`);
      setCurrentYear(year);
    } catch (error) {
      createToastNotification('Something went wrong', 'error');
    }
  };

  const { data: tenantData } = useSWR('/tenant', { revalidateOnFocus: true });

  //set tenant id to redux to be used in setting up schedule in other components
  useEffect(() => {
    if (tenantData) {
      const { id } = tenantData;
      dispatch(setTenantId(id));
    }
  }, [tenantData, dispatch]);

  useEffect(() => {
    setCalendarSchedule(transformCalendarDataData(data));
  }, [data]);

  //opens the mdal to set availability for 1 specific date
  const setOpenAvailability = (
    date: string,
    open: boolean,
    intervals: IntervalsObject[],
  ) => {
    setAvailability({
      date,
      isOpen: open,
      intervals: convertTimeRangeHourFormat(intervals, true),
    });
  };

  return (
    <Stack
      ref={containterRef}
      sx={{
        width: '100%',
      }}
    >
      {isAdmin && (
        <Stack
          flexGrow={1}
          direction='row'
          justifyContent='space-between'
          alignItems='center'
          padding='20px'
        >
          <SwitchViewComponent
            selected={view}
            setSelected={setView}
            refreshCalendarEvents={mutate}
          />
        </Stack>
      )}
      <Divider />
      {view === 'calendar' && (
        <CalendarView
          loading={isLoading}
          view={view}
          availability={calendarSchedule}
          setOpenAvailability={setOpenAvailability}
          setMonthYear={setMonthYear}
        />
      )}
      {view === 'list' && <ListCalendarView />}
      {availability.isOpen && availability.date && isAdmin && (
        <EditAvailabilityModal
          open={availability.isOpen}
          setClose={() => setOpenAvailability('', false, [])}
          date={availability.date}
          refresh={mutate}
        />
      )}
    </Stack>
  );
};

export default AvailabilityCalendar;
